import { useEffect, useState } from "react";
import { Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { toast } from "sonner";

const FIELDS = [
  ["company_name", "Nom entreprise", "DIGITAL GRAPHIC AGENCY"],
  ["company_address", "Adresse", "12 rue… 75000 Paris"],
  ["company_phone", "Port.", "06 12 34 56 78"],
  ["company_email", "Mail", "contact@digitalandgraphic.com"],
  ["company_tva_intra", "N° TVA Intracommunautaire", "FR12345678901"],
  ["company_siret", "N° SIRET", "123 456 789 00012"],
  ["company_naf", "Code NAF", "7311Z"],
  ["ca_objectif_annuel", "Objectif annuel de CA (€)", "80000"],
  ["ca_objectif_mensuel", "Objectif mensuel de CA (€)", "7500"],
];

export default function CompanySettings() {
  const [form, setForm] = useState({});
  const [tpl, setTpl] = useState("");
  const [mentions, setMentions] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const f = {};
      for (const [k] of FIELDS) f[k] = r.data[k] ?? "";
      setForm(f);
      setTpl(r.data.doc_email_template || "");
      setMentions(r.data.quote_legal_mentions || "");
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/settings", { ...form, doc_email_template: tpl, quote_legal_mentions: mentions });
      toast.success("Informations entreprise enregistrées");
    } catch { toast.error("Erreur lors de l'enregistrement"); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-xl shadow-sm p-5 space-y-4" data-testid="company-settings">
      <div>
        <h2 className="font-heading font-bold flex items-center gap-2">
          <Building2 className="w-4 h-4 text-neutral-400" /> Comptabilité — Informations entreprise
        </h2>
        <p className="text-xs text-neutral-400 mt-0.5">
          Coordonnées DIGITAL GRAPHIC AGENCY affichées sur les devis, factures et avoirs (figées dans chaque document émis).
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, label, ph]) => (
          <div key={k}>
            <Label className="text-xs">{label}</Label>
            <Input value={form[k] || ""} onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
              placeholder={ph} className="mt-1" data-testid={`company-${k}`} />
          </div>
        ))}
      </div>
      <div>
        <Label className="text-xs">Mail type d'envoi (devis / facture / avoir)</Label>
        <Textarea rows={5} value={tpl} onChange={(e) => setTpl(e.target.value)} className="mt-1" data-testid="company-email-template"
          placeholder={"Bonjour {client},\n\nVeuillez trouver ci-joint votre {document} N° {numero}.\n\nNous restons à votre disposition pour toute question.\n\nBien cordialement,\nDIGITAL GRAPHIC AGENCY"} />
        <p className="text-[11px] text-neutral-400 mt-1">
          Variables disponibles : {"{client}"} (nom du client), {"{numero}"} (numéro du document), {"{document}"} (« devis », « facture » ou « avoir »).
          Ce message sera pré-rempli dans la fenêtre « Aperçu & envoi » — modifiable à chaque envoi.
        </p>
      </div>
      <div>
        <Label className="text-xs">Mentions légales des devis (imprimées sur le PDF)</Label>
        <Textarea rows={3} value={mentions} onChange={(e) => setMentions(e.target.value)} className="mt-1" data-testid="company-quote-legal-mentions"
          placeholder="Ex : Devis valable 30 jours. Toute commande implique l'acceptation des présentes conditions. Acompte non remboursable en cas d'annulation." />
        <p className="text-[11px] text-neutral-400 mt-1">Affichées dans le bloc « Conditions de paiement » du PDF de chaque devis.</p>
      </div>
      <Button onClick={save} disabled={saving} size="sm" className="bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="company-save">
        {saving ? "Enregistrement…" : "Enregistrer"}
      </Button>
    </div>
  );
}
